/**
 * DeductionRateFormDialog — bulk edit (quality_value, quality_rate) cho
 * 1 (parameter, field_value).
 *
 * Bám Access `frmFactoryQryDeductionRealAdd` (gọi từ btnEdit / btnNew của
 * frmFactoryDeductionRealSubForm):
 *   - Edit: field_value LOCKED (Access line 851); rows pre-fill từ existing.
 *   - New: field_value editable; rows từ master template (value=0/rate=0).
 *   - mc_percent: prefilled từ existing hoặc 0 cho new.
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import { FORM_GRID_3COL, TEXT_TRUNCATE } from '@/theme/responsive'

export interface DeductionRateBulkRow {
  quality_code: string
  quality_value: number | null
  quality_rate: number | null
}

interface DeductionRateFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  loading: boolean
  saving: boolean
  canEdit: boolean
  factoryCode: string
  parameterCode: string
  parameterName: string
  fieldName: string
  fieldValue: number
  fieldValueLocked: boolean
  mcPercent: number | null
  rows: DeductionRateBulkRow[]
  onClose: () => void
  onSave: (payload: {
    fieldValue: number
    mcPercent: number | null
    rows: DeductionRateBulkRow[]
  }) => void
}

interface DraftRow {
  quality_code: string
  quality_value: string
  quality_rate: string
}

const titleBtnSx = {
  py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.4, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
}

// Skill `/convert-form` readonly bg standard — neutral gray.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
  },
}

const stringifyValue = (v: number | null): string =>
  v == null ? '' : String(v)

export default function DeductionRateFormDialog({
  open,
  mode,
  loading,
  saving,
  canEdit,
  factoryCode,
  parameterCode,
  parameterName,
  fieldName,
  fieldValue,
  fieldValueLocked,
  mcPercent,
  rows,
  onClose,
  onSave,
}: DeductionRateFormDialogProps) {
  const [draft, setDraft] = useState<DraftRow[]>([])
  const [fvInput, setFvInput] = useState('')
  const [mcInput, setMcInput] = useState('')
  const [validationError, setValidationError] = useState('')

  useEffect(() => {
    if (!open) return
    setDraft(
      rows.map((r) => ({
        quality_code: r.quality_code,
        quality_value: stringifyValue(r.quality_value),
        quality_rate: stringifyValue(r.quality_rate),
      })),
    )
    setFvInput(String(fieldValue))
    setMcInput(mcPercent == null ? '' : String(mcPercent))
    setValidationError('')
  }, [open, rows, fieldValue, mcPercent])

  const handleCellChange = (idx: number, key: 'quality_value' | 'quality_rate', value: string) => {
    setDraft((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], [key]: value }
      return next
    })
  }

  const handleSave = () => {
    if (!canEdit || saving) return
    const fvNum = Number(fvInput.trim())
    if (!Number.isFinite(fvNum)) {
      setValidationError('Field value must be a number')
      return
    }
    let mcNum: number | null = null
    if (mcInput.trim() !== '') {
      const n = Number(mcInput.trim())
      if (!Number.isFinite(n)) {
        setValidationError('MC % must be a number')
        return
      }
      mcNum = n
    }
    for (let i = 0; i < draft.length; i += 1) {
      const r = draft[i]
      const qv = r.quality_value.trim()
      const qr = r.quality_rate.trim()
      if (qv !== '' && !Number.isFinite(Number(qv))) {
        setValidationError(`${r.quality_code}: Value must be a number`)
        return
      }
      if (qr !== '' && !Number.isFinite(Number(qr))) {
        setValidationError(`${r.quality_code}: Rate must be a number`)
        return
      }
    }
    setValidationError('')
    onSave({
      fieldValue: fvNum,
      mcPercent: mcNum,
      rows: draft.map((r) => ({
        quality_code: r.quality_code,
        quality_value: r.quality_value.trim() === '' ? null : Number(r.quality_value),
        quality_rate: r.quality_rate.trim() === '' ? null : Number(r.quality_rate),
      })),
    })
  }

  const headerLabel = useMemo(() => {
    const verb = mode === 'new' ? 'New' : 'Edit'
    return parameterName
      ? `${verb} — ${parameterCode} · ${parameterName}`
      : `${verb} — ${parameterCode}`
  }, [mode, parameterCode, parameterName])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(720px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
      >
        <Typography
          component="span"
          noWrap
          title={headerLabel}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {headerLabel}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canEdit || saving || loading}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          ...compactInputSx,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          pt: 1.5,
        }}
      >
        {/* Locked header info */}
        <Box sx={{ ...FORM_GRID_3COL, mb: 1.25 }}>
          <TextField
            label="Parameter"
            size="small"
            fullWidth
            value={parameterCode}
            InputProps={{ readOnly: true }}
            disabled
            sx={readonlyFieldSx}
          />
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={parameterName}
            InputProps={{ readOnly: true }}
            disabled
            sx={readonlyFieldSx}
          />
          <TextField
            label="Field"
            size="small"
            fullWidth
            value={fieldName}
            InputProps={{ readOnly: true }}
            disabled
            sx={readonlyFieldSx}
          />
          <TextField
            label="Field Value"
            size="small"
            fullWidth
            type="number"
            value={fvInput}
            onChange={(e) => setFvInput(e.target.value)}
            disabled={!canEdit || fieldValueLocked}
            inputProps={{ style: { textAlign: 'right' } }}
          />
          <TextField
            label="MC %"
            size="small"
            fullWidth
            type="number"
            value={mcInput}
            onChange={(e) => setMcInput(e.target.value)}
            disabled={!canEdit}
            inputProps={{ style: { textAlign: 'right' } }}
          />
          <Box />
        </Box>

        {validationError ? (
          <Alert severity="error" sx={{ mb: 1, py: 0.25 }}>
            {validationError}
          </Alert>
        ) : null}

        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 0.5 }}
        >
          Rates by Quality Code
        </Typography>

        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Loading detail…
            </Typography>
          </Stack>
        ) : draft.length === 0 ? (
          <Alert severity="info" sx={{ py: 0.5 }}>
            No visible quality codes for this factory.
          </Alert>
        ) : (
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: '33%',
                      bgcolor: 'background.paper',
                      fontWeight: 700,
                      borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
                      zIndex: 2,
                    }}
                  >
                    Quality Code
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '33%',
                      bgcolor: 'background.paper',
                      fontWeight: 700,
                      borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
                      zIndex: 2,
                    }}
                  >
                    Value
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '34%',
                      bgcolor: 'background.paper',
                      fontWeight: 700,
                      borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
                      zIndex: 2,
                    }}
                  >
                    Rate
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {draft.map((r, idx) => (
                  <TableRow key={r.quality_code} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{r.quality_code}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={r.quality_value}
                        onChange={(e) => handleCellChange(idx, 'quality_value', e.target.value)}
                        disabled={!canEdit || saving}
                        placeholder="—"
                        sx={compactInputSx}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={r.quality_rate}
                        onChange={(e) => handleCellChange(idx, 'quality_rate', e.target.value)}
                        disabled={!canEdit || saving}
                        placeholder="—"
                        sx={compactInputSx}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  )
}
